import type { OptionProps } from 'react-apollo';
import { graphql } from 'react-apollo';

import { branch, compose, withProps } from 'recompose';

import type { BannerAdProps } from 'bundles/promo-units/components/banner-ad';
import { BANNER_AD_COUNTS_BY_PLACEMENT } from 'bundles/promo-units/components/banner-ad';
import BannerAdRow from 'bundles/promo-units/components/banner-ad/BannerAdRow';
import type {
  BackgroundColor,
  BannerAdPlacement,
  BannerAdRowProps,
} from 'bundles/promo-units/components/banner-ad/constants';
import SoapboxCampaignsQuery from 'bundles/promo-units/components/banner-ad/constants/BannerAdCampaign.graphql';
import type {
  BannerAdCampaign_BannerAdCampaignFragment as BannerAdCampaign,
  BannerAdCampaignQuery as Query,
} from 'bundles/promo-units/components/banner-ad/constants/__generated__/BannerAdCampaign';
import inServerContext from 'bundles/ssr/util/inServerContext';

type PropsFromCaller = {
  placement: BannerAdPlacement;
  forceOpenInNewTab?: boolean;
};

interface QueryVariables {
  placement: string;
  bannerCount: number;
}

export type PropsFromGraphql = {
  loading?: boolean;
  adData: Array<BannerAdProps>;
};

export const propToApiParams = ({ placement }: PropsFromCaller) => ({
  // cache in SSR for performance, but always fetch in the client to get randomized banners
  fetchPolicy: 'network-only' as const, // Only fetches during CSR. Want to always fetch random banners, which doesn't work with caching.
  variables: {
    placement,
    bannerCount: BANNER_AD_COUNTS_BY_PLACEMENT[placement],
  },
  context: { clientName: 'gatewayGql' },
});

const bannerAdCampaignToRowProps = ({
  callToActionUrl,
  creative,
  id,
  promotedEntityId,
  placement,
}: BannerAdCampaign): BannerAdProps =>
  Object.assign({}, creative, {
    trackingData: {
      id,
      promotedEntityId,
      placement: placement as BannerAdPlacement,
      callToActionTarget: callToActionUrl,
      title: creative?.titleText,
    },
    callToActionUrl,
    backgroundColor: creative?.backgroundColor as BackgroundColor,
  });

export const queryResultToBannerAdRowProps = (props: OptionProps<{}, Query, QueryVariables>): PropsFromGraphql => {
  const elements: BannerAdCampaign[] | undefined = props?.data?.BannerAdCampaign?.queryByPlacement?.filter(
    (x): x is BannerAdCampaign => x != null || x !== undefined
  );

  return {
    adData: elements ? elements.map<BannerAdProps>(bannerAdCampaignToRowProps) : [],
    loading: props.data?.loading,
  };
};

export const withBannerAdsFromGql = graphql<PropsFromCaller, Query, QueryVariables, PropsFromGraphql>(
  SoapboxCampaignsQuery,
  {
    options: propToApiParams,
    props: queryResultToBannerAdRowProps,
  }
);

const emptyAdData: Array<BannerAdProps> = [];

const propsToShowLoadingPlaceholder = {
  loading: true,
  adData: emptyAdData,
};

export const withBannerAdDataForPlacement = compose<BannerAdRowProps, PropsFromCaller>(
  withProps<PropsFromGraphql, Record<string, unknown>>(propsToShowLoadingPlaceholder),
  branch<PropsFromCaller>(
    () => !inServerContext, // don't call graphql at all during SSR
    withBannerAdsFromGql
  )
);

export default withBannerAdDataForPlacement(BannerAdRow);
